import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Trash2, Save, Users, Package, Globe } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { adminCountryPackages, adminCreatePackage, adminUpdatePackage, adminDeletePackage, adminCountryLeads } from "@/lib/api";

const BLANK = { name: "", category: "Websites", tagline: "", features: [], price_amount: 0, price_unit: "one-time", price_display: "Starting at", order: 100, active: true, popular: false };

export default function AdminCountry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code] = useState("in");
  const [tab, setTab] = useState("packages");
  const [pkgs, setPkgs] = useState(null);
  const [leads, setLeads] = useState(null);
  const [creating, setCreating] = useState(null);

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);

  const load = useCallback(() => {
    adminCountryPackages(code).then((d) => setPkgs(d.packages || [])).catch(() => setPkgs([]));
    adminCountryLeads(code).then((d) => setLeads(d.leads || [])).catch(() => setLeads([]));
  }, [code]);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (user === undefined || user === null || user.role !== "admin")
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-country">
      <div className="bg-[#0B203E] text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <Globe size={22} className="text-blue-300" />
          <div>
            <h1 className="font-heading text-2xl font-bold">Country Admin — India</h1>
            <p className="text-blue-100/70 text-sm">Manage WebsiteTech India packages, ₹ pricing and leads (isolated by country).</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("packages")} data-testid="country-tab-packages" className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${tab === "packages" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}><Package size={15} /> Packages & Pricing</button>
          <button onClick={() => setTab("leads")} data-testid="country-tab-leads" className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${tab === "leads" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}><Users size={15} /> Leads {leads ? `(${leads.length})` : ""}</button>
        </div>

        {tab === "packages" && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => setCreating({ ...BLANK })} data-testid="country-add-package" className="inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-xl px-4 py-2.5 text-sm"><Plus size={16} /> Add package</button>
            </div>
            {pkgs === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand" size={24} /></div> : (
              <div className="space-y-3" data-testid="country-packages-list">
                {creating && <PackageRow code={code} initial={creating} isNew onSaved={() => { setCreating(null); load(); }} onCancel={() => setCreating(null)} />}
                {pkgs.map((p) => <PackageRow key={p.id} code={code} initial={p} onSaved={load} />)}
              </div>
            )}
          </div>
        )}

        {tab === "leads" && (
          leads === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand" size={24} /></div> :
          leads.length === 0 ? <p className="text-slate-500 text-center py-16">No India leads yet.</p> : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-shadow" data-testid="country-leads-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Location</th><th className="text-left p-3">Vertical</th><th className="text-left p-3">Interest</th><th className="text-left p-3">Source</th></tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-slate-100" data-testid="country-lead-row">
                      <td className="p-3 font-semibold text-slate-800">{l.name}<div className="text-xs text-slate-400 font-normal">{l.business_name}</div></td>
                      <td className="p-3 text-slate-600">{l.email}<div className="text-xs text-slate-400">{l.phone}</div></td>
                      <td className="p-3 text-slate-600">{[l.city, l.state].filter(Boolean).join(", ") || "—"}</td>
                      <td className="p-3 text-slate-600">{l.vertical || "—"}</td>
                      <td className="p-3 text-slate-600">{l.package_interest || l.service_interest || "—"}</td>
                      <td className="p-3 text-slate-400 text-xs">{l.source || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function PackageRow({ code, initial, isNew, onSaved, onCancel }) {
  const [p, setP] = useState({ ...initial, features: (initial.features || []).join("\n") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    const payload = { ...p, price_amount: Number(p.price_amount) || 0, order: Number(p.order) || 100, features: p.features.split("\n").map((s) => s.trim()).filter(Boolean) };
    delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload._id; delete payload.country_id; delete payload.currency; delete payload.currency_symbol; delete payload.slug;
    try {
      if (isNew) await adminCreatePackage(code, payload);
      else await adminUpdatePackage(code, initial.id, payload);
      toast.success(isNew ? "Package created" : "Saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };
  const del = async () => {
    if (!window.confirm(`Delete "${initial.name}"?`)) return;
    try { await adminDeletePackage(code, initial.id); toast.success("Deleted"); onSaved(); } catch { toast.error("Delete failed"); }
  };

  const inp = "bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 card-shadow" data-testid="country-package-row">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <input value={p.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" className={inp} data-testid="pkg-name" />
        <input value={p.category} onChange={(e) => set("category", e.target.value)} placeholder="Category" className={inp} />
        <div className="flex gap-2">
          <input type="number" value={p.price_amount} onChange={(e) => set("price_amount", e.target.value)} placeholder="₹ Amount" className={`${inp} w-full`} data-testid="pkg-price" />
          <select value={p.price_unit} onChange={(e) => set("price_unit", e.target.value)} className={inp}>
            <option value="one-time">one-time</option><option value="month">/mo</option><option value="year">/yr</option><option value="custom">custom</option>
          </select>
        </div>
        <input type="number" value={p.order} onChange={(e) => set("order", e.target.value)} placeholder="Order" className={inp} />
      </div>
      <input value={p.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Tagline" className={`${inp} w-full mt-3`} />
      <textarea value={p.features} onChange={(e) => set("features", e.target.value)} rows={3} placeholder="One feature per line" className={`${inp} w-full mt-3 resize-none`} />
      <div className="flex items-center gap-4 mt-3">
        <label className="text-sm text-slate-600 flex items-center gap-2"><input type="checkbox" checked={p.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        <label className="text-sm text-slate-600 flex items-center gap-2"><input type="checkbox" checked={p.popular} onChange={(e) => set("popular", e.target.checked)} /> Popular</label>
        <div className="ml-auto flex gap-2">
          {onCancel && <button onClick={onCancel} className="text-sm text-slate-500 px-3 py-2">Cancel</button>}
          {!isNew && <button onClick={del} data-testid="pkg-delete" className="text-rose-500 p-2 hover:bg-rose-50 rounded-lg"><Trash2 size={16} /></button>}
          <button onClick={save} disabled={busy} data-testid="pkg-save" className="inline-flex items-center gap-1.5 bg-brand text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
